import { ArcRotateCamera, PointerInfo, Observer, Tools, Engine } from '@babylonjs/core';

const POINTER_EVENT_TYPES = {
	POINTERDOWN: 0x01,
	POINTERUP: 0x02,
	POINTERMOVE: 0x04,
};

interface PointerInput {
	x: number;
	y: number;
	pointerId: number;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	type: any;
}

export class ArcRotateCameraPointerInput {
	public camera: ArcRotateCamera;
	public buttons = [0];

	public angularSensibilityX: number;
	public angularSensibilityY: number;

	private pointerInput: (p: PointerInfo) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private onLostFocus: ((e: FocusEvent) => any) | null;
	private pointerObserver: Observer<PointerInfo> | null;
	private resizeObserver: Observer<Engine> | null;
	private pointer: PointerInput | null;
	private clientRect: ClientRect;

	constructor() {
		this.angularSensibilityX = 1000.0;
		this.angularSensibilityY = 1000.0;
	}

	public getClassName(): string {
		return 'ArcRotateCameraPointerInput';
	}

	public getTypeName(): string {
		return 'ArcRotateCameraPointerInput';
	}

	public getSimpleName(): string {
		return 'pointer';
	}

	public attachControl(noPreventDefault?: boolean): void {
		const engine = this.camera.getEngine();

		const element = document.getElementsByTagName('kompis-view-3d')[0]
			? (document.getElementsByTagName('kompis-view-3d')[0].shadowRoot
					?.childNodes[5] as HTMLElement)
			: null;

		if (!this.resizeObserver) {
			this.resizeObserver = engine.onResizeObservable.add(() => {
				const client = engine.getRenderingCanvasClientRect();
				if (client) {
					this.clientRect = client;
				}
			});
		}
		this.pointer = null;

		this.pointerInput = (p: PointerInfo): void => {
			const evt = p.event as PointerEvent;
			const rect = this.getCanvasClientRect();
			const half = rect.bottom / 2;

			if (p.type !== POINTER_EVENT_TYPES.POINTERMOVE && this.buttons.indexOf(evt.button) === -1) {
				return;
			}

			const srcElement = (evt.srcElement || evt.target) as HTMLElement;

			if (engine.isPointerLock) {
				const offsetX =
					evt.movementX || evt.mozMovementX || evt.webkitMovementX || evt.msMovementX || 0;
				const offsetY =
					evt.movementY || evt.mozMovementY || evt.webkitMovementY || evt.msMovementY || 0;

				this.onPointerMove(offsetX, offsetY);
				this.pointer = null;
			} else if (p.type === POINTER_EVENT_TYPES.POINTERDOWN && srcElement) {
				try {
					srcElement.setPointerCapture(evt.pointerId);
				} catch (e) {
					// Nothing to do with the error.
				}

				if (this.pointer === null) {
					this.pointer = {
						x: evt.clientX,
						y: evt.clientY,
						pointerId: evt.pointerId,
						type: evt.pointerType,
					};
				}

				if (!noPreventDefault) {
					evt.preventDefault();
					if (element) {
						element.focus();
					}
				}
			} else if (p.type === POINTER_EVENT_TYPES.POINTERUP && srcElement) {
				try {
					srcElement.releasePointerCapture(evt.pointerId);
				} catch (e) {
					// Nothing to do with the error.
				}

				this.pointer = null;

				if (!noPreventDefault) {
					evt.preventDefault();
				}
			} else if (p.type === POINTER_EVENT_TYPES.POINTERMOVE) {
				if (!noPreventDefault) {
					evt.preventDefault();
				}
				const modifier = evt.clientY > half ? 1 : -1;
				if (this.pointer) {
					const offsetX = evt.clientX - this.pointer.x;
					const offsetY = evt.clientY - this.pointer.y;
					this.onPointerMove(offsetX * modifier, offsetY);

					this.pointer.x = evt.clientX;
					this.pointer.y = evt.clientY;
				}
			}
		};

		this.pointerObserver = this.camera
			.getScene()
			.onPointerObservable.add(
				this.pointerInput,
				POINTER_EVENT_TYPES.POINTERDOWN |
					POINTER_EVENT_TYPES.POINTERUP |
					POINTER_EVENT_TYPES.POINTERMOVE
			);

		this.onLostFocus = (): void => {
			this.pointer = null;
		};
		if (element) {
			element.addEventListener(
				'contextmenu',
				this.onContextMenu.bind(this) as EventListener,
				false
			);
		}
		Tools.RegisterTopRootEvents(window, [{ name: 'blur', handler: this.onLostFocus }]);
	}

	public detachControl(): void {
		if (window && this.onLostFocus) {
			Tools.UnregisterTopRootEvents(window, [{ name: 'blur', handler: this.onLostFocus }]);
		}
		const element = document.getElementsByTagName('kompis-view-3d')[0]
			? (document.getElementsByTagName('kompis-view-3d')[0].shadowRoot
					?.childNodes[5] as HTMLElement)
			: null;

		if (element && this.pointerObserver) {
			this.camera.getScene().onPointerObservable.remove(this.pointerObserver);
			this.pointerObserver = null;

			if (this.onContextMenu) {
				element.removeEventListener('contextmenu', this.onContextMenu as EventListener);
			}

			this.onLostFocus = null;
		}
	}

	private onPointerMove(offsetX: number, offsetY: number): void {
		this.camera.inertialAlphaOffset -= offsetX / this.angularSensibilityX;
		this.camera.inertialBetaOffset -= offsetY / this.angularSensibilityY;
	}

	private onContextMenu(evt: PointerEvent): void {
		evt.preventDefault();
	}

	private getCanvasClientRect(): ClientRect {
		if (this.clientRect) {
			return this.clientRect;
		}

		const client = this.camera.getEngine().getRenderingCanvasClientRect();
		if (client) {
			this.clientRect = client;
			return this.clientRect;
		}

		throw 'ArcRotateCameraPointerInput: getCanvasClientRect(): client does not exist.';
	}
}
